import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Divider,
  Box,
  Typography,
  Button,
  Stack,
  DialogTitle,
  Tab,
  Tabs,
  DialogContent,
  Tooltip,
  IconButton,
  DialogActions,
  Snackbar,
  Dialog
} from '@material-ui/core';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { formatCPUToCores } from '../../utils/formatNumber';

const YAML = require('yaml');

VMTemplateSummary.propTypes = {
  vm: PropTypes.object,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func
};

export default function VMTemplateSummary({ vm, isOpen, setIsOpen }) {
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(YAML.stringify(vm, null, 2));
    setCopied(true);
  };
  const handleCloseModal = () => {
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
      <DialogTitle>VM Template Summary</DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Detail" />
        <Tab label="YAML View" />
      </Tabs>

      <DialogContent dividers>
        <Box sx={{ maxHeight: 700, overflowY: 'auto' }}>
          {tab === 0 && <BeautyView vm={vm} />}
          {tab === 1 && (
            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Copy YAML">
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                  onClick={handleCopy}
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <YamlView vm={vm} />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button data-testid="close-template-preview" onClick={handleCloseModal}>
          Close
        </Button>
      </DialogActions>

      <Snackbar
        open={copied}
        autoHideDuration={1500}
        handleCloseModal={() => setCopied(false)}
        onClose={() => setCopied(false)}
        message="YAML copied!"
      />
    </Dialog>
  );
}

YamlView.propTypes = {
  vm: PropTypes.object
};

function YamlView({ vm }) {
  return (
    <Box
      component="pre"
      sx={{
        backgroundColor: '#0f172a',
        color: '#e5e7eb',
        p: 2,
        borderRadius: 1,
        fontSize: 13,
        minHeight: 650,
        overflow: 'auto'
      }}
    >
      {YAML.stringify(vm, null, 2)}
    </Box>
  );
}

BeautyView.propTypes = {
  vm: PropTypes.object
};

function BeautyView({ vm }) {
  const formatTrunks = (trunks) => trunks.join(', ');

  return (
    <Box>
      <Grid container alignItems="center" pt={3} pb={3} spacing={4}>
        <Grid item pr={5}>
          <Stack spacing={1.5}>
            <Row label="Name" value={vm.name} />
            <Row label="Replicas" value={vm.max_replicas} />
            <Row label="Label" value={vm.label} />
          </Stack>
        </Grid>

        <Grid item>
          <Stack spacing={1.5}>
            <Row label="Architecture" value={vm.architecture} />
            <Row label="Description" value={vm.description} />
            <Row label="Identity" value={vm.provisioner_identity} />
          </Stack>
        </Grid>
        <Grid item alignSelf="flex-start">
          <Stack spacing={1.5}>
            <Row label="Connectivity" value={(vm.connectivity ?? false).toString()} />
          </Stack>
        </Grid>
      </Grid>

      <Section title="Disks">
        {vm.disks?.map((disk, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {i === 0 ? 'OS Disk' : 'Data Disk'}
            </Typography>

            <Grid container spacing={2} fullWidth>
              <Grid item xs={3}>
                <Row
                  sx={{ width: '150px' }}
                  label="Size"
                  value={`${disk.diskSize} (${disk.size})`}
                />
              </Grid>
              <Grid item xs={9} sx={{ overflowY: 'auto' }}>
                <Box sx={{ whiteSpace: 'nowrap', overflow: 'auto' }}>
                  <Row label="Image" value={disk.image ?? 'Blank Disk'} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Section>
      <Section title="Network Ports">
        {vm.network?.ports?.map((port, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Port {i + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Row label="Name" value={port.name} />
              </Grid>
              <Grid item xs={6} sm={3}>
                {port.vlan_mode && <Row label="VLAN Mode" value={port.vlan_mode} />}
              </Grid>
              <Grid item xs={6} sm={3}>
                {port.tag && <Row label="VLAN Tag" value={port.tag} />}
              </Grid>
              <Grid item xs={6} sm={3}>
                {port.trunks && <Row label="Trunks" value={formatTrunks(port.trunks)} />}
              </Grid>
            </Grid>
          </Box>
        ))}
      </Section>
      <Section title="Minimum VM Resources">
        <Row label="CPU (# cores)" value={vm.min_cpu ? formatCPUToCores(vm.min_cpu) : '-'} />
        <Row label="Memory (MB)" value={vm.min_memory ? vm.min_memory : '-'} />
      </Section>
      {vm.initScript && (
        <Section title="Init Script">
          <ScriptList scripts={vm.initScript} />
        </Section>
      )}
      {vm.bootScript && (
        <Section title="Boot Script">
          <ScriptList scripts={vm.bootScript} />
        </Section>
      )}
    </Box>
  );
}

Section.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node
};

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>
      <Divider sx={{ my: 1 }} />
      {children}
    </Box>
  );
}

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

function Row({ label, value }) {
  return (
    <Stack direction="row" sx={{ gap: 1 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        {label}:
      </Typography>
      <Typography variant="subtitle2" fontWeight={400}>
        {value}
      </Typography>
    </Stack>
  );
}

ScriptList.propTypes = {
  scripts: PropTypes.arrayOf(PropTypes.string)
};

function ScriptList({ scripts }) {
  return (
    <Box
      component="ul"
      sx={{
        backgroundColor: '#f8fafc',
        borderRadius: 1,
        pl: 3
      }}
    >
      {scripts?.map((line, i) => (
        <li key={i}>
          <Typography fontFamily="monospace" variant="body2">
            {line}
          </Typography>
        </li>
      ))}
    </Box>
  );
}
